import React, { useState, useCallback, useEffect } from "react";
import { useHistory } from "./HistoryContext"; // Assuming this is your context
import { useNavigate } from "react-router-dom";
import "./HistoryPage.css";

const HistoryPage = ({ user }) => {
  const { history, deleteFromHistory } = useHistory();
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [confirmModal, setConfirmModal] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const navigate = useNavigate();
  const [longPressTimer, setLongPressTimer] = useState(null);

  const startLongPress = (itemTitle) => {
    const timer = setTimeout(() => {
      setSelectionMode(true);
      toggleItemSelection(itemTitle);
    }, 500);
    setLongPressTimer(timer);
  };

  const endLongPress = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const toggleItemSelection = useCallback((itemTitle) => {
    setSelectedItems((prev) => {
      const updated = new Set(prev);
      if (updated.has(itemTitle)) updated.delete(itemTitle);
      else updated.add(itemTitle);
      return updated;
    });
  }, []);

  const handleItemClick = useCallback(
    (itemSrc, itemTitle) => {
      if (selectionMode) {
        toggleItemSelection(itemTitle);
      } else {
        navigate(`/video/${encodeURIComponent(itemTitle)}`, {
          state: { videoSrc: itemSrc, videoTitle: itemTitle, userInteracted: true },
        });
      }
    },
    [navigate, selectionMode, toggleItemSelection]
  );

  const deleteSingleItem = (itemTitle) => setConfirmModal({ type: "single", target: itemTitle });
  const deleteSelectedItems = () => selectedItems.size > 0 && setConfirmModal({ type: "selected" });
  const deleteAllItems = () => history.length > 0 && setConfirmModal({ type: "all" });

  const confirmDeletion = useCallback(() => {
    if (!confirmModal) return;
    switch (confirmModal.type) {
      case "single":
        deleteFromHistory(confirmModal.target);
        setSelectedItems((prev) => {
          const updated = new Set(prev);
          updated.delete(confirmModal.target);
          return updated;
        });
        break;
      case "selected":
        selectedItems.forEach((title) => deleteFromHistory(title));
        setSelectedItems(new Set());
        setSelectionMode(false);
        break;
      case "all":
        history.forEach((item) => deleteFromHistory(item.title));
        setSelectedItems(new Set());
        setSelectionMode(false);
        break;
      default:
        break; // No-op for unexpected cases, satisfying ESLint
    }
    setConfirmModal(null);
  }, [confirmModal, deleteFromHistory, history, selectedItems]);

  const cancelDeletion = () => setConfirmModal(null);

  const extractVideoId = (url) => {
    if (!url) return "";
    const regex = /(?:youtube\.com\/(?:[^/]+\/[^/]+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\\s]{11})/i;
    const match = url.match(regex);
    return match ? match[1] : url.split("/embed/")[1]?.split("?")[0] || "";
  };

  useEffect(() => {
    if (selectedItems.size === 0) {
      setSelectionMode(false);
    }
  }, [selectedItems]);

  return (
    <div className="watch-history-container">
      <header className="watch-history-header">
        <h1>Watch History</h1>
      </header>
      <div className="actions-container">
        <button
          onClick={deleteSelectedItems}
          className="action-button delete-selected"
          disabled={selectedItems.size === 0}
          aria-label={`Delete ${selectedItems.size} selected items`}
        >
          Delete Selected ({selectedItems.size})
        </button>
        <button
          onClick={deleteAllItems}
          className="action-button delete-all"
          disabled={history.length === 0}
          aria-label="Clear all history"
        >
          Clear All
        </button>
      </div>

      {history.length > 0 ? (
        <ul className="history-items-list">
          {history.map((item, index) => (
            <li key={`${item.title}-${index}`} className="item-card">
              {selectionMode && (
                <input
                  type="checkbox"
                  checked={selectedItems.has(item.title)}
                  onChange={() => toggleItemSelection(item.title)}
                  className="item-checkbox"
                  aria-label={`Select ${item.title}`}
                />
              )}
              <div
                onClick={() => handleItemClick(item.src, item.title)}
                onTouchStart={() => startLongPress(item.title)}
                onTouchEnd={endLongPress}
                onMouseDown={() => startLongPress(item.title)}
                onMouseUp={endLongPress}
                onMouseLeave={endLongPress}
                className="item-content"
                role="button"
                tabIndex={0}
                onKeyPress={(e) => e.key === "Enter" && handleItemClick(item.src, item.title)}
                aria-label={`Play ${item.title}`}
              >
                <img
                  src={`https://img.youtube.com/vi/${extractVideoId(item.src)}/0.jpg`}
                  alt={item.title}
                  className="item-thumbnail"
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = "https://via.placeholder.com/120x68?text=No+Thumbnail";
                  }}
                />
                <div className="item-details">
                  <span className="item-title">{item.title}</span>
                  {item.type && <span className="item-type">({item.type})</span>}
                </div>
              </div>
              <button
                onClick={() => deleteSingleItem(item.title)}
                className="delete-item-button"
                aria-label={`Delete ${item.title} from history`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m-8 0h8m-10 4v12a2 2 0 002 2h8a2 2 0 002-2V10m-5 0v10m-4-10v10" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="no-history-message">
          <p>No videos yet</p>
          <span>Start watching to build your history.</span>
        </div>
      )}

      {confirmModal && (
        <div className="modal-overlay">
          <div className="modal-dialog">
            <h3>Confirm Deletion</h3>
            <p className="modal-text">
              {confirmModal.type === "single" && `Delete "${confirmModal.target}" from your history?`}
              {confirmModal.type === "selected" && `Delete ${selectedItems.size} selected item(s)?`}
              {confirmModal.type === "all" && `Clear all ${history.length} items from your history?`}
            </p>
            <div className="modal-actions">
              <button onClick={confirmDeletion} className="modal-button confirm" aria-label="Confirm deletion">
                Yes
              </button>
              <button onClick={cancelDeletion} className="modal-button cancel" aria-label="Cancel deletion">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryPage;